/**
 * Config.js defines all data and data-interactions for Configurations.
 *
 * A configuration is a collection of profiles containing initial decisions,
 * evaluation factors, and a decision matrix. Configurations are tied to a
 * specific tenantId.
 *
 * @author Sophia Frankel <sophia.frankel@quavo.com>
 */

import ConfigService from '@/services/ConfigService.js';
import ChampionChallengerService from '@/services/ChampionChallengerService.js';
import InvestigationResultService from '@/services/InvestigationResultService.js';
import MetaDataService from '@/services/MetaDataService.js';

export const state = {
	overallConfiguration: {},	// overall configuration object based on tenantId
	historicalAuditLog: [],	// historical audit log for the overall configuration
	profileList: [],	// list of profiles for the overall configuration
	deployedProfileHistoryList: null,	// list of previously deployed profiles for the overall config
	availableFactorList: [],	// list of available evaluation factors
	availableFactorListMap: {},	// map of the available evaluation factors with factorId as the key
	initialDecisionConfigList: [],	// list of available initial decisions
	initialDecisionConfigMap: {},	// map of available initial decisions with initialDecisionConfigId as the key
	initialDecisionOutcomeList: [],	// list of possible initial decision outcomes
	initialDecisionOutcomeListLabels: [],	// list of possible initial decision outcome labels

	configProfile: {},	// profile configuration
	evaluationList: [],	// profile list of evaluation factors
	profileAuditLog: [],	// profile audit log
	profileAuditMessages: [],	// messages to be added to the profile audit log
	initialDecisionItems: [],	// list of initial decisions for the profile
	availableDecisionsList: [],	// list of avaiable initial decisions
	decisionMatrix: [],	// profile's decision matrix
	factorTree: [],	// profile's factor tree
	parentChildrenMap: {},	// profile's parent/children map with factorId of the parent as the key
	factorConfigurations: [],	// flat list of profile's evaluation factors
	factorConfigMap: {},	// map of profile's evaluation factors with factorId as the key
	addableFriendlyFraudGroupsAndFactors: [],	// friendly fraud groups and factors available to add to the factor config
	addableTrueFraudGroupsAndFactors: [],	// true fraud groups and factors available to add to the factor config
	addableFriendlyFraudFactorsOnly: [],	// friendly fraud factors available to add to the factor config
	addableTrueFraudFactorsOnly: [],	// true fraud factors available to add to the factor config
	editingTreeDepth: 0,	// config tree depth being edited
	configurationValid: true,	// configuration validation flag
	thresholdFormValid: true,	// threshold validation flag
	initialDecisionsValid: true,	// initial decision validation flag
	allowProfileDeploy: false,	// allow profile deploy flag
	hasProfileChanged: false,	// profile changed flag
	profileToEditLabel: "",	// label for the profile being edited

	challengerProfile: {},	// 'old' profile configuration object to run champion challenger against
	challengerDateList: [],	// available date ranges for CC
	challengerDateListLabels: [],	// labels for the available date ranges for CC
	challengerSuccessful: false,	// challenger successful flag
	disputeCount: "",	// current dispute count for CC
	challengerRange: "",	// selected CC date range
	challengerTenantId: "",	// selected CC tenant for admin challenges
	challengerStartDate: "",	// CC start date
	challengerEndDate: "",	// CC end date
	challengerRunning: false,	// challenger running flag
	challengerReportId: "",	// report Id generated from CC
	disputesCompleted: 0,	// completed number of disputes while CC is running
	challengerResultList: [],	// results list generated from CC
	challengerDifferentOutcomeList: [],	// list of disputes with a different outcome
	challengerOldDecisionCounts: {},	// CC old decision counts
	challengerNewDecisionCounts: {},	// CC new decision counts
	challengerOldDecisionPercentages: {},	// CC old decision percentages
	challengerNewDecisionPercentages: {},	// CC new decision percentages
	challengerDecisionDeltaPercentages: {},	// CC delta percentages
	challengerStatusTimer: 0,	// identifier for checking the challenger status
	challengerDisputeDetailsOld: {},	// CC old dispute details
	challengerDisputeDetailsNew: {},	// CC new dispute details
	challengerDisputeDetailsOverallResultsOld: {},	// CC old overall results
	challengerDisputeDetailsOverallResultsNew: {},	// CC new overall results
	challengerDisputeDetailsFactorDecisionsOld: [],	// CC old factor decisions
	challengerDisputeDetailsFactorDecisionsNew: [],	// CC new factor decisions
	challengerDisputeDetailsFactorDecisionMapOld: [],	// CC old factor decisions map
	challengerDisputeDetailsFactorDecisionMapNew: [],	// CC new factor decisions map
	challengerDisputeDetailsFactorChanges: [],	// CC list of factor changes

	profilePerformanceClaimData: {},	// profile performance claim data
	profilePerformanceDisputeData: {},	// profile performance dispute data
	profilePerformancePaidDisputeAverage: 0,	// profile performance paid dispute average
	profilePerformanceSeries: [],	// profile performance dispute results series
	profilePerformanceDisputeAmounts: {},	// profile performance dispute amounts

	initialDecisionMetaDataDetails: {},	// initial decision meta data details
	factorMetaDataDetails: {},	// factor decision meta data details
	factorMetaDataBuilt: false,	// factor meta data built flag
	hideInitialDecisions: true	// hide initial decisions flag

};

/**
 *	Actions hook up to the interface, but they do not touch the store data directly.
 *	They can be rather complicated and will generally lean on one or more mutations.
 *	Actions are also the communication layer with external sources (API).
 *
 *	Actions come with access to four contexts ({ state, getters, commit, dispatch })
 *		state = direct data access... avoid use
 *		getters = indirect data access... this is good
 *		commit = call a mutation
 *		dispatch = for us, dispatch will almost certainly be an axios api call
*/
export const actions = {
	/**
	 * Retrieve the overall configuration based on the provided payload
	 * Reformat the profile history list
	 * Retrieve the expected profile
	 *
	 * @param {*} contexts - commit, dispatch
	 * @param {*} payload - payload containing the active tenantId and the URL route
	 */
	retrieveOverallConfig({ commit, dispatch }, payload) {
		ConfigService.getOverallConfig(payload.tenantId, dispatch).then(async (response) => {
			await commit('GET_OVERALL_CONFIG', response.data);
			await dispatch("reformatProfileHistoryList");
			let profileList = response.data.profileList;
			if (payload.tenantId === 'master') {
				// Find default master profile in the profileList
				let defaultMasterProfile = profileList.find(profile => profile.masterDefault === true);
				let masterPayload = {
					tenantId: payload.tenantId,
					profileId: defaultMasterProfile.profileId,
					client: "Default"
				};

				// check for params indicating a different profileId
				if (payload.route.params.id !== undefined && payload.route.name !== "Configuration Factors & Decisions") {
					masterPayload.profileId = payload.route.params.id;
				}

				await dispatch("retrieveDefaultMasterProfile", masterPayload);
			}
			else {
				let liveProfile = profileList.find(profile => profile.isLive === true);
				let livePayload = {
					tenantId: payload.tenantId,
					profileId: liveProfile.profileId,
					client: "Default"
				};

				// check for params indicating a different profileId
				if (payload.route.params.id !== undefined) {
					livePayload.profileId = payload.route.params.id;
				}

				await dispatch("retrieveProfile", livePayload);
				// check for if you're viewing a historical profile
				if (payload.route.name !== undefined && payload.route.name === "Configuration History" && payload.route.params.id !== undefined) {
					await dispatch("retrieveProfilePerformance", livePayload);
				}
			}
		}).catch(error => {
			console.error("There was an error retrieving overall config:" + error.response);
		});
	},
	/**
	 * Create a map of the available factor list
	 * Dispatch the following actions:
	 * 	mapFactorConfiguration
	 *  createParentChildrenMap
	 *  createFactorConfigMap
	 * 	createAddableGroupsAndFactors
	 * 	createInitialDecisionOutcomeListLabels
	 *
	 * @param {*} contexts - commit, getters, dispatch
	 */
	async mapAvailableFactorList({ commit, getters, dispatch }) {
		var AFL = getters.getAvailableFactorList;

		var availableFactorListMap = {};
		AFL.map((factor) => {
			return availableFactorListMap[factor.factorId] = factor;
		})

		await commit("MAP_AFL", availableFactorListMap);
		await dispatch("mapFactorConfiguration");
		await dispatch("createParentChildrenMap");
		await dispatch("createFactorConfigMap");
		await dispatch("createAddableGroupsAndFactors");
		await dispatch("createInitialDecisionOutcomeListLabels");

		if (getters.getFactorMetaDataDetails.factorId !== undefined) {
			await dispatch("buildFactorMetaDataDetails");
		}
	},
	/**
	 * Retrieve a profile based on the provided payload
	 * Dispatch the following actions:
	 * 	(optional) setProfileToEditLabel
	 * 	(optional) setChallengerProfile
	 * 	mapMatrixFromProfile
	 * 	mapAvailableFactorList
	 * 	createInitialDecisionConfigMap
	 * 	extractChallengerLabels
	 *
	 * @param {*} contexts - commit, dispatch, getters
	 * @param {Object} payload -  payload containing a tenantId, profileId, and client
	 */
	retrieveProfile({ commit, dispatch, getters }, payload) {
		ConfigService.getProfile(payload, dispatch).then(async (response) => {
			commit('GET_PROFILE', response.data);
			if (!response.data.isLive) {
				dispatch("setProfileToEditLabel", response.data.profileLabel);
				if (getters.getChallengerProfile.profileId === undefined && payload.tenantId !== 'master') {
					payload.profileId = getters.getOverallConfig.profileList.find(profile => profile.isLive === true).profileId;
					dispatch("setChallengerProfile", payload);
				}
			} else {
				commit('SET_CHALLENGER_PROFILE', response.data);
			}

			await dispatch("mapMatrixFromProfile");
			await dispatch("mapAvailableFactorList");
			await dispatch("createInitialDecisionConfigMap");
			await dispatch("extractChallengerLabels");
		}).catch(error => {
			console.error("There was an error retrieving profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve the profile',
			});
		});
	},
	/**
	 * Retrieve a default master profile based on the provided payload
	 * Dispatch the following actions:
	 * 	(optional) setProfileToEditLabel
	 * 	mapMatrixFromProfile
	 * 	mapAvailableFactorList
	 * 	createInitialDecisionConfigMap
	 * 	extractChallengerLabels
	 *
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - payload containing a tenantId, profileId, and client
	 */
	retrieveDefaultMasterProfile({ commit, dispatch }, payload) {
		ConfigService.getProfile(payload, dispatch).then(async (response) => {
			await commit('GET_PROFILE', response.data);
			if (!response.data.masterDefault) {
				await dispatch("setProfileToEditLabel", response.data.profileLabel);
			}
			await dispatch("mapMatrixFromProfile");
			await dispatch("mapAvailableFactorList");
			await dispatch("createInitialDecisionConfigMap");
			await dispatch("extractChallengerLabels");
		}).catch(error => {
			console.error("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve the default master profile',
			});
		});
	},
	/**
	 * Delete an existing profile
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - profileId and tenantId of profile to be deleted
	 */
	deleteProfile({ commit, dispatch }, payload) {
		ConfigService.deleteProfile(payload, dispatch).then(response => {
			if (response !== undefined) {
				commit("DELETE_PROFILE", payload.profileId);
				dispatch("notifications/addNotification", {
					status: 'success',
					message: 'Profile successfully deleted (' + payload.profileId + ')',
				});
			}
		}).catch(error => {
			console.error("There was an error deleting the profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to delete the profile',
			});
		});
	},
	/**
	 * Update the selected profile in the Configuration Explorer
	 * @param {*} contexts - commit, dispatch, getters
	 */
	updateProfile({ commit, dispatch, getters }) {
		let payload = {
			profile: getters.getProfile,
			auditMessages: getters.getProfileAuditMessages
		};

		ConfigService.updateProfile(payload, dispatch).then((response) => {
			if (response !== undefined) {
				commit('SET_PROFILE_CHANGED', false);
				dispatch("notifications/addNotification", {
					status: 'success',
					message: 'The profile has been updated',
				});
			}
		}).catch(error => {
			console.error("There was an error updating the profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to update the profile',
			});
		});
	},
	/**
	 * Set the hasProfileChanged flag
	 * @param {*} contexts - commit
	 * @param {Boolean} value - true if the current profile has been modified
	 */
	setProfileChanged({ commit }, value) {
		commit('SET_PROFILE_CHANGED', value);
	},
	/**
	 * Add a message to the profile audit log messages
	 * @param {*} contexts - commit
	 * @param {String} message - new audit message
	 */
	appendProfileAuditMessages({ commit }, message) {
		commit('APPEND_PROFILE_AUDIT_MESSAGES', message);
	},
	/**
	 * Clear the profile audit messages
	 * @param {*} contexts - commit
	 */
	clearProfileAuditMessages({ commit }) {
		commit('CLEAR_PROFILE_AUDIT_MESSAGES');
	},
	/**
	 * Configure a copy of a profile based on the provided payload
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - identifying information for the new profile
	 */
	configureCopyProfile({ commit, dispatch }, payload) {
		ConfigService.copyProfile(payload, dispatch).then(response => {
			payload.response = response;
			commit('CONFIGURE_COPY_PROFILE', payload);
			dispatch("notifications/addNotification", {
				status: 'success',
				message: 'New profile copy created (' + response.data.profileId + ')',
			});
		}).catch(error => {
			console.error("There was an error copying the profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to copy the profile',
			});
		});
	},
	/**
	 * Rename a profile
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - profileId, tenantId, and new name
	 */
	renameProfile({ commit, dispatch }, payload) {
		ConfigService.renameProfile(payload, dispatch).then((response) => {
			if (response !== undefined) {
				commit('RENAME_PROFILE', payload);
				dispatch("notifications/addNotification", {
					status: 'success',
					message: 'Renamed profile (' + payload.profileId + ')',
				});
			}
		}).catch(error => {
			console.error("There was an error renaming the profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while renaming your profile',
			});
		});
	},
	/**
	 * Decode the decision matrix into usable data
	 * @param {*} contexts - commit, getters
	 */
	mapMatrixFromProfile({ commit, getters }) {
		var profile = getters.getProfile;
		var evaluationList = profile.overallDecisionConfiguration.evaluationList;

		var matrix = evaluationList.map((item) => {
			var factorTreeId1 = item.factorTreeResultList[0].factorTreeId;
			var factorTreeResult1 = item.factorTreeResultList[0].factorTreeResult;
			var factorTreeId2 = item.factorTreeResultList[1].factorTreeId;
			var factorTreeResult2 = item.factorTreeResultList[1].factorTreeResult;

			return {
				decision: item.decision,
				cell: (factorTreeId1 == "FriendlyFraud" ? "F" : "T") + (factorTreeResult1 == "Low" ? "L" : (factorTreeResult1 == "Medium" ? "M" : "H")) +
					(factorTreeId2 == "FriendlyFraud" ? "F" : "T") + (factorTreeResult2 == "Low" ? "L" : (factorTreeResult2 == "Medium" ? "M" : "H")),
			}
		})
		commit('MAP_MATRIX', matrix);
	},
	/**
	 * Map the decision matrix back to the API format
	 * @param {*} contexts - commit, getters
	 */
	mapMatrixBack({ commit, getters }) {
		var decisionMatrix = getters.getDecisionMatrix;

		var matrix = decisionMatrix.map((item) => {
			var factorTreeId1 = item.cell[0] == "T" ? "TrueFraud" : "FriendlyFraud";
			var factorTreeResult1 = item.cell[1] == "L" ? "Low" : (item.cell[1] == "M" ? "Medium" : "High");
			var factorTreeId2 = item.cell[2] == "T" ? "TrueFraud" : "FriendlyFraud";
			var factorTreeResult2 = item.cell[3] == "L" ? "Low" : (item.cell[3] == "M" ? "Medium" : "High");

			return {
				decision: item.decision,
				factorTreeResultList: [
					{
						factorTreeId: factorTreeId1,
						factorTreeResult: factorTreeResult1
					},
					{
						factorTreeId: factorTreeId2,
						factorTreeResult: factorTreeResult2
					}
				]
			}
		})
		commit('MAP_MATRIX_BACK', matrix);
	},
	/**
	 * Map the factor configuration thresholds using a breadth-first traversal
	 * @param {*} contexts - commit, getters
	 */
	mapFactorConfiguration({ commit, getters }) {
		var AFM = getters.getAvailableFactorListMap;
		var factorConfiguration = getters.getProfile.overallDecisionConfiguration.factorConfiguration;

		var factorThresholds = [];
		var factorQueue = [];
		factorQueue.push(factorConfiguration.rootFactorTreeList);

		var root = factorQueue[0];
		var childCount = root.length;
		for (var i = 0; i < childCount; i++) {
			factorQueue.push(root[i]);
		}
		factorQueue.shift();

		while (factorQueue.length !== 0) {
			var node = factorQueue[0];
			if (node.factorTreeList !== undefined) {
				childCount = node.factorTreeList.length;
				for (i = 0; i < childCount; i++) {
					factorQueue.push(node.factorTreeList[i]);
				}
			}
			// visit front node

			// find factor details
			var details = AFM[node.factorConfiguration.factorId];

			// add details to node
			if (details !== undefined) {
				factorQueue[0].factorConfiguration = {
					factorId: node.factorConfiguration.factorId,
					functionName: node.factorConfiguration.functionName,
					parentId: (node.factorConfiguration.parentId !== undefined ? node.factorConfiguration.parentId : "root"),
					thresholds: node.factorConfiguration.thresholds.map(threshold => {
						var thresholdDetails = details.thresholds.find(thres => thres.thresholdId === threshold.thresholdId);

						if (thresholdDetails !== undefined) {
							return {
								label: thresholdDetails.label,
								placeholder: thresholdDetails.placeholder,
								thresholdId: thresholdDetails.thresholdId,
								levels: threshold.levels,
								orderingType: thresholdDetails.orderingType,
								validateRegex: thresholdDetails.validateRegex
							}
						}
						else {
							return threshold;
						}

					}),
					label: details.label,
					treeMode: details.treeMode,
					type: details.type,
					scope: details.scope,
					category: details.category,
					description: details.description,
					whyItWorks: details.whyItWorks,
					knowledgeBaseUrl: details.knowledgeBaseUrl,
					isActive: details.isActive,
				}
			}

			factorThresholds.push(factorQueue.shift());
		}

		commit('MAP_FACTOR_CONFIGURATION', factorThresholds);
	},
	/**
	 * Map the factor configuration back to the API format
	 * Dispatch: updateProfile
	 * @param {*} contexts - getters, commit, dispatch
	 */
	mapFactorConfigurationBack({ getters, commit, dispatch }) {
		// recreate factor tree from ParentChildrenMap
		var PCM = getters.getParentChildrenMap;

		var newFactorTree = {
			rootFactorTreeList: [
				{
					factorTreeList: [],
					factorTreeId: "FriendlyFraud",
					factorConfiguration: {}
				},
				{
					factorTreeList: [],
					factorTreeId: "TrueFraud",
					factorConfiguration: {}
				}
			]
		}

		var parentIdList = [];
		parentIdList.push('root');

		while (parentIdList.length > 0) {

			var parentId = parentIdList.shift();

			if (PCM[parentId] !== undefined) {
				for (var i = 0; i < PCM[parentId].length; i++) {
					var child = PCM[parentId][i];

					//visit node
					if (child.treeMode == "Root") {
						if (child.factorId == "FriendlyFraud") {
							newFactorTree.rootFactorTreeList[0].factorConfiguration = child;
							if (PCM[child.factorId] != undefined) {
								PCM[child.factorId].map(kid => {
									newFactorTree.rootFactorTreeList[0].factorTreeList.push({
										factorConfiguration: kid,
										factorTreeList: (PCM[kid.factorId] !== undefined ? PCM[kid.factorId].map(grandKid => {
											return {
												factorConfiguration: grandKid
											};
										}) : undefined
										)
									})
								})
							}
						} else {
							newFactorTree.rootFactorTreeList[1].factorConfiguration = child;
							if (PCM[child.factorId] != undefined) {
								PCM[child.factorId].map(kid => {
									newFactorTree.rootFactorTreeList[1].factorTreeList.push({
										factorConfiguration: kid,
										factorTreeList: (PCM[kid.factorId] !== undefined ? PCM[kid.factorId].map(grandKid => {
											return {
												factorConfiguration: grandKid
											};
										}) : undefined
										)
									})
								})
							}
						}
					}
				}
			}
		}

		commit('MAP_FACTOR_CONFIGURATION_BACK', newFactorTree);
		dispatch("updateProfile");

	},
	/**
	 * Create a map with the parentId as the key and the child factors as the value
	 * @param {*} contexts - commit, getters
	 */
	createParentChildrenMap({ commit, getters }) {
		var factorConfigs = getters.getFactorConfigurations;

		var parentChildrenMap = {};

		factorConfigs.map(node => {
			if (!parentChildrenMap[node.factorConfiguration.parentId]) {
				parentChildrenMap[node.factorConfiguration.parentId] = [];
			}
			parentChildrenMap[node.factorConfiguration.parentId].push(node.factorConfiguration);
		})

		commit("CREATE_PARENT_CHILDREN_MAP", parentChildrenMap);

	},
	/**
	 * Create a factor configuration map with the factorId as the key
	 * and the factorConfiguration as the value
	 * @param {*} contexts - commit, getters
	 */
	createFactorConfigMap({ commit, getters }) {
		var factorConfigs = getters.getFactorConfigurations;

		var map = {};

		factorConfigs.map(node => {
			if (!map[node.factorConfiguration.factorId]) {
				map[node.factorConfiguration.factorId] = [];
			}
			map[node.factorConfiguration.factorId].push(node.factorConfiguration);
		})

		commit("CREATE_FACTOR_CONFIG_MAP", map);
	},
	/**
	 * Create two lists: addableFriendlyFraudGroupsAndFactors and addableTrueFraudGroupsAndFactors
	 * These lists represent the factors that may be added to the current factor configuration
	 * Dispatch: createFactorsOnlyList
	 * @param {*} contexts - commit, getters, dispatch
	 */
	createAddableGroupsAndFactors({ commit, getters, dispatch }) {
		let factorConfigMap = getters.getFactorConfigMap;
		let AFL = getters.getAvailableFactorList;

		let friendlyFraudFactors = [];
		let trueFraudFactors = [];

		AFL.map(node => {
			if (factorConfigMap[node.factorId] === undefined) {
				switch (node.type) {
					case "Friendly Fraud":
						if (node.isActive) {
							friendlyFraudFactors.push(node);
						}
						break;
					case "True Fraud":
						if (node.isActive) {
							trueFraudFactors.push(node);
						}
						break;
					default:
						break;
				}
			}
		});

		commit("CREATE_ADDABLE_FRIENDLY_FRAUD_GROUPS_AND_FACTORS", friendlyFraudFactors);
		commit("CREATE_ADDABLE_TRUE_FRAUD_GROUPS_AND_FACTORS", trueFraudFactors);
		dispatch('createFactorsOnlyList');
	},
	/**
	 * Create lists for friendly fraud factors and true fraud factors.
	 * These lists represent the factors that can be added to the specified groups
	 * @param {*} contexts - commit, getters
	 */
	createFactorsOnlyList({ commit, getters }) {
		let addableFriendlyFraudGroupsAndFactors = getters.getAddableFriendlyFraudGroupsAndFactors;
		let addableTrueFraudGroupsAndFactors = getters.getAddableTrueFraudGroupsAndFactors;

		let friendlyFraudFactors = [];
		let trueFraudFactors = [];

		addableFriendlyFraudGroupsAndFactors.map((groupOrFactor) => {
			if (groupOrFactor.treeMode === "Child") {
				friendlyFraudFactors.push(groupOrFactor);
			}
		});

		addableTrueFraudGroupsAndFactors.map((groupOrFactor) => {
			if (groupOrFactor.treeMode === "Child") {
				trueFraudFactors.push(groupOrFactor);
			}
		});

		commit('CREATE_FRIENDLY_FRAUD_FACTORS_ONLY_LIST', friendlyFraudFactors);
		commit('CREATE_TRUE_FRAUD_FACTORS_ONLY_LIST', trueFraudFactors);
	},
	/**
	 * Create a map of the initial decisions with the initialDecisionConfigId as the key
	 * and the decision object as the value
	 * Dispatch: createInitialDecisionItems
	 * @param {*} contexts - commit, getters, dispatch
	 */
	async createInitialDecisionConfigMap({ commit, getters, dispatch }) {
		var initialDecisionConfigList = getters.getInitialDecisionConfigList;

		var map = {};
		initialDecisionConfigList.map(decision => {
			map[decision.initialDecisionConfigId] = decision;
		});

		await commit("CREATE_INITIAL_DECISION_CONFIG_MAP", map);
		dispatch("createInitialDecisionItems");
	},
	/**
	 * Create a list of the initial decision outcome list labels
	 * @param {*} contexts - commit, getters
	 */
	createInitialDecisionOutcomeListLabels({ commit, getters }) {
		var initialDecisionOutcomeList = getters.getInitialDecisionOutcomeList;

		var labels = initialDecisionOutcomeList.map(outcome => {
			return outcome.label;
		})

		commit("CREATE_INITIAL_DECISION_OUTCOME_LIST_LABELS", labels);
	},
	/**
	 * Map the evaluationList with metadata from the initialDecisionConfigList
	 * to create the initial decision items
	 * Dispatch: createAvialableDecisionsList
	 * @param {*} contexts - commit, getters, dispatch
	 */
	createInitialDecisionItems({ commit, getters, dispatch }) {
		var evaluationList = getters.getEvaluationList;
		var initialDecisionConfigMap = getters.getInitialDecisionConfigMap;
		var initialDecisionOutcomeList = getters.getInitialDecisionOutcomeList;

		// map evaluationList with metadata from the initialDecisionConfigList
		var initialDecisionItems = evaluationList.map(decision => {
			var metaData = initialDecisionConfigMap[decision.initialDecisionConfigId];

			var rightSideOption = metaData.rightSideOptions[0];
			if (decision.rightSideOptionId !== undefined && decision.rightSideOptionId !== "") {
				rightSideOption = metaData.rightSideOptions.find(option => option.optionId === decision.rightSideOptionId);
			}

			var operatorIndex = metaData.operatorOptions.findIndex(op => op.operator === decision.operator);

			return {
				priority: decision.priority,
				label: metaData.label,
				initialDecisionConfigId: decision.initialDecisionConfigId,
				value: ((rightSideOption.value === undefined) && (decision.value !== "string") ? decision.rightSideValue : ""),
				placeholder: (rightSideOption.placeholder !== undefined ? rightSideOption.placeholder : ''),
				operator: decision.operator,
				operatorLabel: metaData.operatorOptions[operatorIndex].label,
				decision: decision.decision,
				decisionLabel: initialDecisionOutcomeList.find(outcome => outcome.initialDecisionOutcomeId === decision.decision).label,
				operatorOptionsLabels: metaData.operatorOptions.map(option => {
					return option.label;
				}),
				operatorOptions: metaData.operatorOptions,
				rightSideOptionId: rightSideOption.optionId,
				rightSideSelectedOption: rightSideOption.label,
				rightSideOptionsLabels: metaData.rightSideOptions.map(option => {
					return option.label;
				}),
				rightSideOptions: metaData.rightSideOptions,
				isActive: metaData.isActive,
				multipleEntry: metaData.multipleEntry
			}
		});

		commit("CREATE_INITIAL_DECISION_ITEMS", initialDecisionItems);
		dispatch("createAvialableDecisionsList");

	},
	/**
	 * Create the available decisions list to determine which initial decisions
	 * can be added to the current profile
	 * @param {*} contexts - commit, getters
	 */
	createAvialableDecisionsList({ commit, getters }) {
		var initialDecisionItems = getters.getInitialDecisionItems;
		var initialDecisionConfigList = getters.getInitialDecisionConfigList;

		var ADL = [];
		initialDecisionConfigList.map((config) => {
			if ((initialDecisionItems.find((item) => item.initialDecisionConfigId === config.initialDecisionConfigId) === undefined)
				|| (config.multipleEntry === true)) {
					ADL.push(config);
			}
		});

		commit("CREATE_AVAILABLE_DECISIONS_LIST", ADL);
	},
	/**
	 * Recreate the evaluation list to the API format
	 * @param {*} contexts - commit, getters
	 */
	updateEvaluationList({ commit, getters }) {
		var initialDecisionItems = getters.getInitialDecisionItems;
		var initialDecisionConfigMap = getters.getInitialDecisionConfigMap;
		var initialDecisionOutcomeList = getters.getInitialDecisionOutcomeList;

		// create new evaluation list
		// map initial decision items to new evaluation list
		var evaluationList = initialDecisionItems.map(item => {

			var metaData = initialDecisionConfigMap[item.initialDecisionConfigId];
			var operator = item.operatorOptions.find(operator => operator.label === item.operatorLabel);
			var rightSideOption = metaData.rightSideOptions.find(option => option.label === item.rightSideSelectedOption);
			var decision = initialDecisionOutcomeList.find(outcome => outcome.label === item.decisionLabel).initialDecisionOutcomeId;

			return {
				decision: decision,
				initialDecisionConfigId: item.initialDecisionConfigId,
				leftSideType: metaData.leftSideType,
				leftSideValue: metaData.leftSideValue,
				operator: operator.operator,
				priority: item.priority,
				rightSideOptionId: rightSideOption.optionId,
				rightSideType: rightSideOption.type,
				rightSideValue: (rightSideOption.value === undefined ? item.value : rightSideOption.value)
			}
		})

		commit("UPDATE_EVALUATION_LIST", evaluationList);
	},
	/**
	 * Set the current editing depth of the configuration tree being viewed
	 * @param {*} contexts - commit
	 * @param {Int} value - tree depth
	 */
	setEditingTreeDepth({ commit }, value) {
		commit('SET_EDITING_TREE_DEPTH', value);
	},
	/**
	 * Deploy a profile as the active profile for the specified tenant
	 * @param {*} contexts - dispatch, getters
	 * @param {*} router - router for application
	 */
	deployProfile({ dispatch, getters }, router) {
		ConfigService.deployProfile(getters.getProfile, dispatch).then((response) => {
			if (response !== undefined) {
				dispatch("notifications/addNotification", {
					status: 'success',
					message: 'The profile has been deployed',
				});
			}
			router.push({ name: "Configuration" });
		}).catch(error => {
			console.error("There was an error deploying the profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to deploy the profile',
			});
		});
	},
	/**
	 * Deploy the default master profile for the master tenant
	 * @param {*} contexts - dispatch, getters
	 * @param {*} router - router for application
	 */
	deployDefaultMasterProfile({ dispatch, getters }, router) {
		ConfigService.deployDefaultMasterProfile(getters.getProfile, dispatch).then((response) => {
			if (response !== undefined) {
				dispatch("notifications/addNotification", {
					status: 'success',
					message: 'The profile has been deployed',
				});
			}
			router.push({ name: "Configuration" });
		}).catch(error => {
			console.error("There was an error deploying the profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to deploy the profile',
			});
		});
	},
	/**
	 * Set the allowProfileDeploy flag
	 * @param {*} contexts - commit
	 * @param {Boolean} value - true if the profile can be deployed
	 */
	setAllowProfileDeploy({ commit }, value) {
		commit('SET_ALLOW_PROFILE_DEPLOY', value);
	},
	/**
	 * Set the configurationValid flag
	 * @param {*} contexts - commit
	 * @param {Boolean} value - true if the configuration being viewed is valid
	 */
	setConfigurationValid({ commit }, value) {
		commit('SET_CONFIGURATION_VALID', value);
	},
	/**
	 * Set the thresholdFormValid flag
	 * @param {*} contexts - commit
	 * @param {Boolean} value - true if the threshold form being viewed is valid
	 */
	setThresholdFormValid({ commit }, value) {
		commit('SET_THRESHOLD_FORM_VALID', value);
	},
	/**
	 * Set the initialDecisionsValid flag
	 * @param {*} contexts - commit
	 * @param {Boolean} value - true if the initial decisions are valid
	 */
	setInitialDecisionsValid({ commit }, value) {
		commit('SET_INITIAL_DECISIONS_VALID', value);
	},
	/**
	 * Clear the factor configurations
	 * @param {*} context - commit
	 */
	clearFactorConfigurations({ commit }) {
		commit('CLEAR_FACTOR_CONFIGURATIONS');
	},
	/**
	 * Clear the config state data
	 * @param {*} contexts - commit
	 */
	clearConfigState({ commit }) {
		commit('CLEAR_CONFIG_STATE');
	},
	/**
	 * Set the profileToEditLabel used in the configuration explorer
	 * @param {*} contexts - commit
	 * @param {String} label - label of the profile being edited
	 */
	setProfileToEditLabel({ commit }, label) {
		commit('SET_PROFILE_TO_EDIT_LABEL', label);
	},
	/**
	 * Set the initial decision items
	 * @param {*} contexts - commit
	 * @param {Array} items - list of initial decision objects
	 */
	setInitialDecisionItems({ commit }, items) {
		commit('CREATE_INITIAL_DECISION_ITEMS', items);
	},
	/**
	 * Reformat the profile history list to display the start and end dates more legibly
	 * @param {*} contexts - commit, getters
	 */
	reformatProfileHistoryList({ commit, getters }) {
		let deployedProfileHistoryList = getters.getDeployedProfileHistoryList;

		deployedProfileHistoryList.map(profile => {
			if (profile.deployStartDateTime) {
				profile.deployStartDateTime = profile.deployStartDateTime.replace("T", " ");
				profile.deployStartDateTime = profile.deployStartDateTime.substr(0, 19);
			}

			if (profile.deployEndDateTime) {
				profile.deployEndDateTime = profile.deployEndDateTime.replace("T", " ");
				profile.deployEndDateTime = profile.deployEndDateTime.substr(0, 19);
			}
		});

		commit("REFORMAT_PROFILE_HISTORY_LIST", deployedProfileHistoryList);
	},
	/**
	 * Set the 'old' profile to run against in CC
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload -  tenantId, profileId, and client of the profile
	 */
	setChallengerProfile({ commit, dispatch }, payload) {
		ConfigService.getProfile(payload, dispatch).then(response => {
			commit("SET_CHALLENGER_PROFILE", response.data);
		}).catch(error => {
			console.error("There was an error setting the challenger profile:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve the profile',
			});
		});
	},
	/**
	 * Initiate the Champion Challenger. Set the reportId and
	 * challenger running flag after recieving a successful response
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - object containing the fields necessary to initiate CC
	 */
	initiateChampionChallenger({ commit, dispatch }, payload) {
		let t0 = performance.now();
		ChampionChallengerService.initiateChampionChallenger(payload, dispatch).then((response) => {
			commit("SET_CHALLENGER_REPORT_ID", response.data.reportId);
			commit("SET_CHALLENGER_RUNNING", true);
			let t1 = performance.now();
			console.info("Champion Challenger Initiate took " + (t1 - t0) + " milliseconds.");
		}).catch(error => {
			console.error("There was an error initiating champion challenger:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to initiate the Champion Challenger.',
			});
		});
	},
	/**
	 * Retrieve the dispute count based on the provided payload
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - tenantId, profileId, start, and end dates
	 */
	retrieveDisputeCount({ commit, dispatch }, payload) {
		ChampionChallengerService.retrieveDisputeCount(payload, dispatch).then(response => {
			commit("SET_DISPUTE_COUNT", response.data.disputeCount);
		}).catch(error => {
			console.error("There was an error retrieving the dispute count:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve the dispute count.',
			});
		});
	},
	/**
	 * Clear the dispute count state variable
	 * @param {*} contexts - commit
	 */
	clearDisputeCount({ commit }) {
		commit("SET_DISPUTE_COUNT", "");
	},
	/**
	 * Set the challenger date list labels to display date ranges for CC
	 * Dispatch: setChallengerRange
	 * @param {*} contexts - commit, dispatch, getters
	 */
	async extractChallengerLabels({ commit, dispatch, getters }) {
		let dateList = getters.getChallengerDateList;

		let labels = [];

		dateList.map(date => {
			labels.push(date.label);
		})

		await commit("SET_CHALLENGER_DATE_LIST_LABELS", labels);
		await dispatch("setChallengerRange", getters.getChallengerDateList[0].value);
	},
	/**
	 * Set the champion challenger date range
	 * Dispatch: calculateStartAndEndDate
	 * @param {*} contexts - commit, dispatch
	 * @param {Int} range - amount of days
	 */
	async setChallengerRange({ commit, dispatch }, range) {
		await commit("SET_CHALLENGER_RANGE", range);
		await dispatch("calculateStartAndEndDate");
	},
	/**
	 * Set challenger tenantId for CC as an admin user
	 * Dispatch: setChallengerProfile
	 * @param {*} contexts - commit, dispatch
	 * @param {String} tenantId - selected tenantId to run CC against
	 */
	async setChallengerTenantId({ commit, dispatch }, tenantId) {
		await commit('SET_CHALLENGER_TENANT_ID', tenantId);
		ConfigService.getOverallConfig(tenantId, dispatch).then(response => {
			let profileList = response.data.profileList;
			let liveProfileId = profileList.find(profile => profile.isLive === true).profileId;

			let payload = {
				tenantId: tenantId,
				profileId: liveProfileId,
				clientId: "Default"
			};

			dispatch('setChallengerProfile', payload);
		})
	},
	/**
	 * Calculate the start and end date for the CC based on the challenger range
	 * @param {*} contexts - commit, getters
	 */
	calculateStartAndEndDate({ commit, getters }) {
		let range = getters.getChallengerRange;

		// calculate end date
		let currentDate = new Date();
		let endFullYear = currentDate.getFullYear();
		let endMonth = parseInt(currentDate.getMonth()) + 1;
		let endDay = currentDate.getDate();

		let endDate = endFullYear + "-" + endMonth + "-" + endDay;
		currentDate.setDate(currentDate.getDate() - range);

		// calculate start date
		let startFullYear = currentDate.getFullYear();
		let startMonth = parseInt(currentDate.getMonth()) + 1;
		let startDay = currentDate.getDate();

		let startDate = startFullYear + "-" + startMonth + "-" + startDay;

		commit("SET_CHALLENGER_START_DATE", startDate);
		commit("SET_CHALLENGER_END_DATE", endDate);
	},
	/**
	 * Clear profile performance state variables
	 * @param {*} contexts - commit
	 */
	async clearProfilePerformance({ commit }) {
		await commit('CLEAR_PROFILE_PERFORMANCE');
	},
	/**
	 * Set the challenger successful flag
	 * @param {*} contexts - commit
	 * @param {Boolean} value - true if CC was successful
	 */
	setChallengerSuccessfulFlag({ commit }, value) {
		commit("SET_CHALLENGER_SUCCESSFUL_FLAG", value);
	},
	/**
	 * Retrieve the CC status and set the number of disputes completed
	 * @param {*} contexts - commit, dispatch, getters
	 * @param {Object} payload - tenantId and reportId generated from CC
	 */
	retrieveChallengerStatus({ commit, dispatch, getters }, payload) {
		if (getters.getChallengerRunning) {
			ChampionChallengerService.retrieveStatus(payload, dispatch).then(async (response) => {
				await commit("SET_DISPUTES_COMPLETED", response.data.completedNumberDisputes);
				if ((getters.getDisputesCompleted >= getters.getDisputeCount) && (getters.getDisputesCompleted !== 0) && (!getters.getChallengerSuccessful)) {
					await commit("SET_CHALLENGER_RUNNING", false);
					await commit("SET_CHALLENGER_SUCCESSFUL_FLAG", true);
					clearInterval(getters.getChallengerStatusTimer);
					await dispatch("notifications/addNotification", {
						status: 'success',
						message: 'Challenger Complete!',
					});
				}
			}).catch(error => {
				console.error("There was an error retrieving challenger status:" + error.response);
				dispatch("notifications/addNotification", {
					status: 'error',
					message: 'An error occurred while attempting to retrieve the challenger status.',
				});
			});
		}
	},
	/**
	 * Activate/Inactivate a profile as an admin user
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - tenantId, profileId, and isActive flag
	 */
	activateProfile({ commit, dispatch }, payload) {
		ConfigService.activateProfile(payload, dispatch).then(async () => {
			await commit('ACTIVATE_PROFILE', payload);
			await dispatch("notifications/addNotification", {
				status: 'success',
				message: (payload.isActive ? 'Activated' : 'Inactivated') + ' profile (' + payload.profileId + ')',
			});
		}).catch(error => {
			console.log("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to activate the profile.',
			});
		});
	},
	/**
	 * Retrieve Challenger result list
	 * Dispatch: sortChallengerResults
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload -  tenantId and reportId generated by CC
	 */
	retrieveChallengerResultList({ commit, dispatch }, payload) {
		ChampionChallengerService.retrieveResultList(payload, dispatch).then(async (response) => {
			await commit('SET_CHALLENGER_RESULT_LIST', response.data.disputeList);
			await dispatch('sortChallengerResults');
		}).catch(error => {
			console.error("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve the challenger result list.',
			});
		});
	},
	/**
	 * Sort the challenger results into old decision counts,
	 * new decision counts, and different outcomes list
	 * Dispatch: calculateChallengerPercentages
	 * @param {*} contexts - commit, getters, dispatch
	 */
	async sortChallengerResults({ commit, getters, dispatch }) {
		let resultList = getters.getChallengerResultList;

		let differentOutcomeList = [];

		let oldDecisionCounts = {
			pay: 0,
			refer: 0,
			deny: 0
		};

		let newDecisionCounts = {
			pay: 0,
			refer: 0,
			deny: 0
		};

		resultList.map(result => {
			let oldDes = result.decisionOld;
			let newDes = result.decisionNew;

			switch (oldDes) {
				case "Pay":
					oldDecisionCounts.pay++;
					break;
				case "Refer":
					oldDecisionCounts.refer++;
					break;
				case "Deny":
					oldDecisionCounts.deny++;
					break;
				default:
					break;
			}

			switch (newDes) {
				case "Pay":
					newDecisionCounts.pay++;
					break;
				case "Refer":
					newDecisionCounts.refer++;
					break;
				case "Deny":
					newDecisionCounts.deny++;
					break;
				default:
					break;
			}

			if (oldDes !== newDes) {
				differentOutcomeList.push(result);
			}
		});

		await commit('SET_CHALLENGER_OLD_DECISION_COUNTS', oldDecisionCounts);
		await commit('SET_CHALLENGER_NEW_DECISION_COUNTS', newDecisionCounts);
		await commit('SET_CHALLENGER_DIFFERENT_OUTCOME_LIST', differentOutcomeList);
		await dispatch('calculateChallengerPercentages');
	},
	/**
	 * Calculate percentages for the CC result charts
	 * @param {*} contexts - commit, getters
	 */
	calculateChallengerPercentages({ commit, getters }) {
		let oldDecisionCounts = getters.getChallengerOldDecisionCounts;
		let newDecisionCounts = getters.getChallengerNewDecisionCounts;
		let resultList = getters.getChallengerResultList;

		let oldDecisionPercentages = {
			pay: 0,
			refer: 0,
			deny: 0
		};

		let newDecisionPercentages = {
			pay: 0,
			refer: 0,
			deny: 0
		};

		let deltaPercentages = {
			pay: 0,
			refer: 0,
			deny: 0
		};

		if (resultList.length > 0) {
			// calculate old decision percentages
			oldDecisionPercentages.pay = (oldDecisionCounts.pay / resultList.length) * 100;
			oldDecisionPercentages.refer = (oldDecisionCounts.refer / resultList.length) * 100;
			oldDecisionPercentages.deny = (oldDecisionCounts.deny / resultList.length) * 100;

			// calculate new decision percentages
			newDecisionPercentages.pay = (newDecisionCounts.pay / resultList.length) * 100;
			newDecisionPercentages.refer = (newDecisionCounts.refer / resultList.length) * 100;
			newDecisionPercentages.deny = (newDecisionCounts.deny / resultList.length) * 100;

			// calculate change in percentages
			deltaPercentages.pay = newDecisionPercentages.pay - oldDecisionPercentages.pay;
			deltaPercentages.refer = newDecisionPercentages.refer - oldDecisionPercentages.refer;
			deltaPercentages.deny = newDecisionPercentages.deny - oldDecisionPercentages.deny;

			commit('SET_CHALLENGER_OLD_DECISION_PERCENTAGES', oldDecisionPercentages);
			commit('SET_CHALLENGER_NEW_DECISION_PERCENTAGES', newDecisionPercentages);
			commit('SET_CHALLENGER_DECISION_DELTA_PERCENTAGES', deltaPercentages);
		}
	},
	/**
	 * Clear Champion Challenger state variables
	 * @param {*} contexts - commit
	 */
	async clearChallengerData({ commit }) {
		await commit('CLEAR_CHALLENGER_DATA');
	},
	/**
	 * Set the challenger status timer to repeatedly check the CC status
	 * @param {*} contexts - commit
	 * @param {Number} value - identifier for the challenger status timer
	 */
	setChallengerStatusTimer({ commit }, value) {
		commit('SET_CHALLENGER_STATUS_TIMER', value);
	},
	/**
	 * Retrieve profile performance for historical profiles
	 * Dispatch: buildProfilePerformance
	 * @param {*} contexts - dispatch
	 * @param {Object} payload - profileId and tenantId of historical profile
	 */
	retrieveProfilePerformance({ dispatch }, payload) {
		InvestigationResultService.retrieveProfilePerformance(payload, dispatch).then(async (response) => {
			await dispatch('buildProfilePerformance', response.data);
		}).catch(error => {
			console.error("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve profile performance data.',
			});
		});
	},
	/**
	 * Build the profile performance arrays, replacing undefined values with zeros
	 * @param {*} contexts - commit
	 * @param {Object} results - data from the retrieveProfilePerformance endpoint
	 */
	buildProfilePerformance({ commit }, results) {
		const claimCount = results.claimData.claimCounts;
		const disputeAmount = results.disputeData.disputeAmounts;
		const disputeCount = results.disputeData.disputeCounts;

		let claimDeny = claimCount.overallDecisionType.Deny !== undefined ? claimCount.overallDecisionType.Deny : 0;
		let claimPay = claimCount.overallDecisionType.Pay !== undefined ? claimCount.overallDecisionType.Pay : 0;
		let claimRefer = claimCount.overallDecisionType.Refer !== undefined ? claimCount.overallDecisionType.Refer : 0;
		let claimTotal = claimCount.total !== undefined ? claimCount.total : 0;

		let disputeAmountDeny = disputeAmount.overallDecision.Deny !== undefined ? disputeAmount.overallDecision.Deny : 0;
		let disputeAmountPay = disputeAmount.overallDecision.Pay !== undefined ? disputeAmount.overallDecision.Pay : 0;
		let disputeAmountRefer = disputeAmount.overallDecision.Refer !== undefined ? disputeAmount.overallDecision.Refer : 0;
		let disputeAmountTotal = disputeAmount.total !== undefined ? disputeAmount.total : 0;

		let disputeCountDeny = disputeCount.overallDecision.Deny !== undefined ? disputeCount.overallDecision.Deny : 0;
		let disputeCountPay = disputeCount.overallDecision.Pay !== undefined ? disputeCount.overallDecision.Pay : 0;
		let disputeCountRefer = disputeCount.overallDecision.Refer !== undefined ? disputeCount.overallDecision.Refer : 0;
		let disputeCountTotal = disputeCount.total !== undefined ? disputeCount.total : 0;

		let claimResults = {
			claimCounts: {
				Deny: claimDeny,
				Pay: claimPay,
				Refer: claimRefer,
				total: claimTotal
			}
		};

		let disputeResults = {
			disputeAmounts: {
				Deny: disputeAmountDeny,
				Pay: disputeAmountPay,
				Refer: disputeAmountRefer,
				total: disputeAmountTotal
			},
			disputeCounts: {
				Deny: disputeCountDeny,
				Pay: disputeCountPay,
				Refer: disputeCountRefer,
				total: disputeCountTotal
			}
		}

		// build dispute data
		// dispute counts
		let performanceSeries = [];
		performanceSeries.push(disputeCountPay);
		performanceSeries.push(disputeCountDeny);
		performanceSeries.push(disputeCountRefer);

		// dispute amounts
		let disputeAmounts = {};
		disputeAmounts["pay"] = disputeAmountPay;
		disputeAmounts["refer"] = disputeAmountRefer;
		disputeAmounts["deny"] = disputeAmountDeny;

		// paid dispute average
		let average = 0;
		if (disputeCountPay > 0) {
			average = disputeAmountPay / disputeCountPay;
		}

		let payload = {
			disputeData: disputeResults,
			performanceSeries: performanceSeries,
			disputeAmounts: disputeAmounts,
			paidDisputeAverage: average
		}

		commit("SET_PROFILE_PERFORMANCE_CLAIM_DATA", claimResults);
		commit("SET_PROFILE_PERFORMANCE_DISPUTE_DATA", payload);
	},
	/**
	 * Retrieve initial decision meta data
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - tenantId and initialDecisionConfigId
	 */
	retrieveInitialDecisionMetaData({ commit, dispatch }, payload) {
		MetaDataService.retrieveInitialDecision(payload, dispatch).then(response => {
			commit('SET_INITIAL_DECISION_META_DATA_DETAILS', response.data);
		}).catch(error => {
			console.error("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve the initial decision meta data.',
			});
		});
	},
	/**
	 * Retrieve evaluation factor meta data
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - tenantId and factorId
	 */
	retrieveFactorMetaData({ commit, dispatch }, payload) {
		MetaDataService.retrieveFactor(payload, dispatch).then(async (response) => {
			await commit('SET_FACTOR_META_DATA_DETAILS', response.data);
		}).catch(error => {
			console.error("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve the factor meta data.',
			});
		});
	},
	/**
	 * Set the initial decision meta data details
	 * @param {*} contexts - commit
	 * @param {Object} details - meta data details
	 */
	setInitialDecisionMetaDataDetails({ commit }, details) {
		commit('SET_INITIAL_DECISION_META_DATA_DETAILS', details);
	},
	/**
	 * Set evaluation factor meta data details
	 * Dispatch: buildFactorMetaDataDetails
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} details - meta data details
	 */
	async setFactorMetaDataDetails({ commit, dispatch }, details) {
		await commit('SET_FACTOR_META_DATA_DETAILS', details);
		await dispatch("buildFactorMetaDataDetails");

	},
	/**
	 * Build factor meta data details by setting the levels and ordering fields
	 * from the available factor list for each threshold
	 * @param {*} context - commit, getters
	 */
	async buildFactorMetaDataDetails({ commit, getters }) {
		let availableFactorListMap = getters.getAvailableFactorListMap;
		let details = getters.getFactorMetaDataDetails;

		let availableFactorMetaData = availableFactorListMap[details.factorId];

		let thresholdMetaDataMap = {};

		availableFactorMetaData.thresholds.map(metaData => {
			thresholdMetaDataMap[metaData.thresholdId] = metaData;
		})

		details.thresholds.map(threshold => {
			if (thresholdMetaDataMap[threshold.thresholdId].orderingType !== undefined) {
				threshold["orderingType"] = thresholdMetaDataMap[threshold.thresholdId].orderingType;
			}
			if (thresholdMetaDataMap[threshold.thresholdId].levels.inputFields !== undefined) {
				// return a string of the levelIds (capitalized) separated by a slash
				let inputFields = thresholdMetaDataMap[threshold.thresholdId].levels.inputFields;

				let thresholdStr = "";
				inputFields.map(field => {
					thresholdStr += field.fieldId[0].toUpperCase() + field.fieldId.slice(1) + " / ";
				})

				// remove last slash
				thresholdStr = thresholdStr.substring(0, thresholdStr.length - 3);
				threshold["levels"] = thresholdStr;
			}
		})

		await commit('SET_FACTOR_META_DATA_DETAILS', details);
		await commit('SET_FACTOR_META_DATA_BUILT_FLAG', true);
	},
	/**
	 * Update the initial decision meta data for a specific decision
	 * @param {*} contexts - dispatch
	 * @param {Object} decision - new initial decision meta data object
	 */
	updateInitialDecisionMetaData({ dispatch }, decision) {
		MetaDataService.updateInitialDecision(decision, dispatch).then(response => {
			dispatch("notifications/addNotification", {
				status: 'success',
				message: decision.label + ": " + response.data.message,
			});
		}).catch(error => {
			console.error("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to update the initial decision meta data.',
			});
		});
	},
	/**
	 * Update factor meta data for a specific evaluation factor
	 * @param {*} contexts - dispatch
	 * @param {Object} factor - new evaluation factor meta data object
	 */
	updateFactorMetaData({ dispatch }, factor) {
		MetaDataService.updateFactor(factor, dispatch).then(response => {
			dispatch("notifications/addNotification", {
				status: 'success',
				message: factor.label + ": " + response.data.message,
			});
		}).catch(error => {
			console.log("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to update the factor meta data.',
			});
		});
	},
	/**
	 * Retrieve CC result details
	 * Dispatch:
	 * 	createChallengerDisputeDetailsOverallResults
	 * 	traverseChallengerFactorDecisionTrees
	 *
	 * @param {*} contexts - commit, dispatch
	 * @param {Object} payload - tenantId, requestId, and requestSubId
	 */
	retrieveChallengerResultDetails({ commit, dispatch }, payload) {
		ChampionChallengerService.retrieveResultDetails(payload, dispatch).then(async (response) => {
			await commit('SET_CHALLENGER_DISPUTE_DETAILS_OLD', response.data.decisionOld);
			await commit('SET_CHALLENGER_DISPUTE_DETAILS_NEW', response.data.decisionNew);
			dispatch('createChallengerDisputeDetailsOverallResults');
			dispatch('traverseChallengerFactorDecisionTrees');
		}).catch(error => {
			console.error("There was an error:" + error.response);
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'An error occurred while attempting to retrieve challenger result details.',
			});
		});
	},
	/**
	 * Create a map of the overall results based on the factor decision lists
	 * @param {*} contexts - commit, getters
	 */
	createChallengerDisputeDetailsOverallResults({ commit, getters }) {
		let oldDetails = getters.getChallengerDisputeDetailsOld;
		let newDetails = getters.getChallengerDisputeDetailsNew;

		let oldFactorDecisionList = oldDetails.factorDecisionList;
		let newFactorDecisionList = newDetails.factorDecisionList;

		let oldDecisionOverallResults = {};
		let newDecisionOverallResults = {};

		oldFactorDecisionList.map(decision => {
			oldDecisionOverallResults[decision.factorId] = {
				factorName: decision.factorName,
				decisionLevel: decision.decisionLevel,
				decisionScore: decision.decisionScore
			};
		});

		newFactorDecisionList.map(decision => {
			newDecisionOverallResults[decision.factorId] = {
				factorName: decision.factorName,
				decisionLevel: decision.decisionLevel,
				decisionScore: decision.decisionScore
			};
		})

		commit('SET_CHALLENGER_DISPUTE_DETAILS_OVERALL_RESULTS_OLD', oldDecisionOverallResults);
		commit('SET_CHALLENGER_DISPUTE_DETAILS_OVERALL_RESULTS_NEW', newDecisionOverallResults);
	},
	/**
	 * Recursive function for traversing the CC decision trees
	 * @param {*} contexts - getters, dispatch, commit
	 * @param {Object} payload - object containing a node and an action
	 * @returns when the node is undefined -- base case
	 */
	factorDecisionRecursionFunction({ getters, dispatch, commit }, payload) {
		let availableFactorListMap = getters.getAvailableFactorListMap;
		let childCount = 0;

		// base case --> return when case is hit
		if (payload.node === undefined) {
			return;
		}
		// visit node
		// use available factor list map to add label and type to node
		let metaData = availableFactorListMap[payload.node.factorId];
		payload.node.label = metaData.label;
		payload.node.type = metaData.type;

		// add to appropriate list
		commit(payload.action, payload.node);

		// get children and recurse
		if (payload.node.factorDecisionList !== undefined) {
			childCount = payload.node.factorDecisionList.length;
			for (let i = 0; i < childCount; i++) {
				// recurse 
				let childPayload = {
					node: payload.node.factorDecisionList[i],
					action: payload.action
				}

				dispatch("factorDecisionRecursionFunction", childPayload);
			}
		}
	},
	/**
	 * Traverse the CC decision trees using a depth-first traversal
	 * Dispatch: createChallengerDisputeDetailsFactorDecisionMaps
	 * @param {*} contexts - getters, dispatch, commit
	 */
	async traverseChallengerFactorDecisionTrees({ getters, dispatch, commit }) {
		let challengerDisputeDetailsOld = getters.getChallengerDisputeDetailsOld;
		let challengerDisputeDetailsNew = getters.getChallengerDisputeDetailsNew;

		let rootFactorDecisionListOld = challengerDisputeDetailsOld.factorDecisionList;
		let rootFactorDecisionListNew = challengerDisputeDetailsNew.factorDecisionList;

		let factorQueueOld = [];
		let factorQueueNew = [];

		await commit("CLEAR_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_OLD");
		await commit("CLEAR_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_NEW");

		// traverse old factor decision tree
		factorQueueOld.push(rootFactorDecisionListOld);

		let rootOld = factorQueueOld[0];
		let childCountOld = rootOld.length;

		for (let i = 0; i < childCountOld; i++) {
			// call recursion function
			let oldPayload = {
				node: rootOld[i],
				action: "APPEND_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_OLD"
			};

			await dispatch("factorDecisionRecursionFunction", oldPayload);
		}

		// traverse new factor decision tree
		factorQueueNew.push(rootFactorDecisionListNew);

		let rootNew = factorQueueNew[0];
		let childCountNew = rootNew.length;

		for (let i = 0; i < childCountNew; i++) {
			// call recursion function
			let newPayload = {
				node: rootNew[i],
				action: "APPEND_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_NEW"
			};

			await dispatch("factorDecisionRecursionFunction", newPayload);
		}

		await dispatch('createChallengerDisputeDetailsFactorDecisionMaps');
	},
	/**
	 * Create a map of the CC dispute details factor decisions
	 * Dispatch: compareChallengerDisputeDetailsFactorDecisions
	 * @param {*} contexts - commit, getters, dispatch
	 */
	async createChallengerDisputeDetailsFactorDecisionMaps({ commit, getters, dispatch }) {
		let factorDecisionsOld = getters.getChallengerDisputeDetailsFactorDecisionsOld;
		let factorDecisionsNew = getters.getChallengerDisputeDetailsFactorDecisionsNew;

		let factorDecisionMapOld = {};
		let factorDecisionMapNew = {};

		factorDecisionsOld.map(decision => {
			factorDecisionMapOld[decision.factorId] = decision;
		});

		factorDecisionsNew.map(decision => {
			factorDecisionMapNew[decision.factorId] = decision;
		});

		await commit('SET_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISION_MAP_OLD', factorDecisionMapOld);
		await commit('SET_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISION_MAP_NEW', factorDecisionMapNew);
		await dispatch('compareChallengerDisputeDetailsFactorDecisions');
	},
	/**
	 * Compare dispute details factor decision lengths
	 * map smaller of the two --> look up factor in other map
	 * check if factor is NOT in larger list --> add to array
	 * check if scores are unequal --> add to array
	 * check for uncommon factors in the larger list --> add to array
	 *
	 * @param {*} contexts - commit, getters
	 */
	compareChallengerDisputeDetailsFactorDecisions({ commit, getters }) {
		let factorDecisionsOld = getters.getChallengerDisputeDetailsFactorDecisionsOld;
		let factorDecisionsNew = getters.getChallengerDisputeDetailsFactorDecisionsNew;

		let factorDecisionMapOld = getters.getChallengerDisputeDetailsFactorDecisionMapOld;
		let factorDecisionMapNew = getters.getChallengerDisputeDetailsFactorDecisionMapNew;

		let factorChanges = [];

		// new factor decisions list is shorter
		if (factorDecisionsOld.length >= factorDecisionsNew.length) {
			factorDecisionsNew.map(decision => {
				if (factorDecisionMapOld[decision.factorId] !== undefined) {
					if ((factorDecisionMapOld[decision.factorId].decisionScore !== decision.decisionScore)
						&& (decision.factorId !== "TrueFraud") && (decision.factorId !== "FriendlyFraud")) {
						factorChanges.push(decision);
					}
				}
				// uncommon factor
				else {
					factorChanges.push(decision);
				}
			});

			// check old factor decisions list for uncommon factors
			factorDecisionsOld.map(decision => {
				if (factorDecisionMapNew[decision.factorId] === undefined) {
					factorChanges.push(decision);
				}
			})
		}
		// old factor decisions list is shorter
		else {
			factorDecisionsOld.map(decision => {
				if (factorDecisionMapNew[decision.factorId] !== undefined) {
					if ((factorDecisionMapNew[decision.factorId].decisionScore !== decision.decisionScore)
						&& (decision.factorId !== "TrueFraud") && (decision.factorId !== "FriendlyFraud")) {
						factorChanges.push(decision);
					}
				}
				// uncommon factor
				else {
					factorChanges.push(decision);
				}
			});

			// check new factor decisions list for uncommon factors
			factorDecisionsNew.map(decision => {
				if (factorDecisionMapOld[decision.factorId] === undefined) {
					factorChanges.push(decision);
				}
			})
		}

		commit('SET_CHALLENGER_DISPUTE_DETAILS_FACTOR_CHANGES', factorChanges);
	}

};
/*
	Mutations are methods that act on the state data directly.
	They should be incredibly basic and perform a single task.

	Mutations always take in the current 'state' and a payload.
*/
export const mutations = {
	/**
	 * Clear all current state data
	 * @param {*} state - current state
	 */
	CLEAR_CONFIG_STATE(state) {
		state.overallConfiguration = {};
		state.profileList = []
		state.deployedProfileHistoryList = [];
		state.availableFactorList = [];
		state.availableFactorListMap = {};
		state.initialDecisionConfigList = [];
		state.initialDecisionConfigMap = {};
		state.initialDecisionOutcomeList = [];
		state.initialDecisionOutcomeListLabels = [];

		state.configProfile = {};
		state.evaluationList = [];
		state.initialDecisionItems = [];
		state.availableDecisionsList = [];
		state.decisionMatrix = [];
		state.factorTree = [];
		state.parentChildrenMap = {};
		state.factorConfigurations = [];
		state.factorConfigMap = {};
		state.addableFriendlyFraudGroupsAndFactors = [];
		state.addableTrueFraudGroupsAndFactors = [];
		state.addableFriendlyFraudFactorsOnly = [];
		state.addableTrueFraudFactorsOnly = [];
		state.addableFactorsOnly = [];
		state.editingTreeDepth = 0;
		state.configurationValid = true;
		state.thresholdFormValid = false;
		state.allowProfileDeploy = false;
		state.hasProfileChanged = false;
		state.profileToEditLabel = "";

		state.challengerProfile = {};
		state.challengerDateList = [];
		state.challengerDateListLabels = [];
		state.challengerSuccessful = false;
		state.disputeCount = "";
		state.challengerRange = "";
		state.challengerStartDate = "";
		state.challengerEndDate = "";
		state.challengerRunning = false;
		state.challengerReportId = "";
		state.disputesCompleted = 0;
		state.challengerResultList = [];
		state.challengerDifferentOutcomeList = [];
		state.challengerOldDecisionCounts = {};
		state.challengerNewDecisionCounts = {};
		state.challengerOldDecisionPercentages = {};
		state.challengerNewDecisionPercentages = {};
		state.challengerDecisionDeltaPercentages = {};
		state.challengerStatusTimer = 0;
		state.challengerDisputeDetailsOld = {};
		state.challengerDisputeDetailsNew = {};
		state.challengerDisputeDetailsOverallResultsOld = {};
		state.challengerDisputeDetailsOverallResultsNew = {};
		state.challengerDisputeDetailsFactorDecisionsOld = [];
		state.challengerDisputeDetailsFactorDecisionsNew = [];
		state.challengerDisputeDetailsFactorDecisionMapOld = [];
		state.challengerDisputeDetailsFactorDecisionMapNew = [];
		state.challengerDisputeDetailsFactorChanges = [];

		state.profilePerformanceClaimData = {};
		state.profilePerformanceDisputeData = {};
		state.profilePerformancePaidDisputeAverage = 0;
		state.profilePerformanceSeries = [];
		state.profilePerformanceDisputeAmounts = {};

		state.initialDecisionMetaDataDetails = {};
		state.factorMetaDataDetails = {};
	},
	/**
	 * Clear profile factor configurations
	 * @param {*} state - current state
	 */
	CLEAR_FACTOR_CONFIGURATIONS(state) {
		state.factorConfigurations = [];
	},
	/**
	 * Clear profile performance state variables
	 * @param {*} state - current state
	 */
	CLEAR_PROFILE_PERFORMANCE(state) {
		state.configProfile = {};
		state.profilePerformanceClaimData = {};
		state.profilePerformanceDisputeData = {};
		state.profilePerformancePaidDisputeAverage = 0;
		state.profilePerformanceSeries = [];
		state.profilePerformanceDisputeAmounts = {};
	},
	/**
	 * Clear challenger state variables
	 * @param {*} state - current state
	 */
	CLEAR_CHALLENGER_DATA(state) {
		state.challengerRunning = false;
		state.challengerSuccessful = false;
		state.disputeCount = "";
		state.challengerReportId = "";
		state.disputesCompleted = 0;
		state.challengerResultList = [];
		state.challengerDifferentOutcomeList = [];
		state.challengerOldDecisionCounts = {};
		state.challengerNewDecisionCounts = {};
		state.challengerOldDecisionPercentages = {};
		state.challengerNewDecisionPercentages = {};
		state.challengerDecisionDeltaPercentages = {};
	},
	/**
	 * Set the overall configuration state variables from the API response
	 * @param {*} state - current state
	 * @param {Object} response - overall configuration for the active tenant
	 */
	GET_OVERALL_CONFIG(state, response) {
		state.overallConfiguration = response;
		state.historicalAuditLog = response.auditLog;
		state.profileList = response.profileList;
		state.deployedProfileHistoryList = response.deployedProfileHistoryList;
		state.availableFactorList = response.availableFactorList;
		state.initialDecisionConfigList = response.initialDecisionConfigList;
		state.initialDecisionOutcomeList = response.initialDecisionOutcomeList;
		state.challengerDateList = response.championChallengerDateList;
	},
	/**
	 * Set the available factor list map
	 * @param {*} state - current state
	 * @param {Map} map - avialable factor list map
	 */
	MAP_AFL(state, map) {
		state.availableFactorListMap = map;
	},
	/**
	 * Set the profile configuration state variables based on the API response
	 * @param {*} state - current state
	 * @param {Object} response - API response from retrieving the selected profile
	 */
	GET_PROFILE(state, response) {
		state.configProfile = response;
		state.factorTree = response.overallDecisionConfiguration.factorConfiguration;
		state.evaluationList = response.initialDecisionConfiguration.evaluationList;
		state.profileAuditLog = (response.auditLog !== undefined ? response.auditLog : []);
	},
	/**
	 * Set the hasProfileChanged flag
	 * @param {*} state - current state
	 * @param {Boolean} value - true if the current profile has been modified
	 */
	SET_PROFILE_CHANGED(state, value) {
		state.hasProfileChanged = value;
	},
	/**
	 * Append a message to the profile audit messages
	 * @param {*} state - current state
	 * @param {String} message - new profile audit log message
	 */
	APPEND_PROFILE_AUDIT_MESSAGES(state, message) {
		state.profileAuditMessages.push(message);
	},
	/**
	 * Clear the profile audit messages
	 * @param {*} state - current state
	 */
	CLEAR_PROFILE_AUDIT_MESSAGES(state) {
		state.profileAuditMessages = [];
	},
	/**
	 * Place the new profile into the profileList after configuring a copy.
	 * We'll place the new item just after the "live" profile
	 * so that new items are always on the front page.
	 *
	 * @param {*} state - current state
	 * @param {Object} payload - profile details
	 */
	CONFIGURE_COPY_PROFILE(state, payload) {
		state.profileList.splice(1, 0, {
			profileId: payload.response.data.profileId,
			profileLabel: payload.name,
			isModifiable: true
		});
	},
	/**
	 * Rename profile - update profile label in the profile list
	 * @param {*} state - current state
	 * @param {Object} payload - profile details
	 */
	RENAME_PROFILE(state, payload) {
		let profile = state.profileList.find(item => item.profileId === payload.profileId);
		profile.profileLabel = payload.name;
	},
	/**
	 * Remove profile from the profile list
	 * @param {*} state - current state
	 * @param {String} profileId - profileId of the profile to be removed
	 */
	DELETE_PROFILE(state, profileId) {
		state.profileList = state.profileList.filter(item => item.profileId !== profileId);
	},
	/**
	 * Set the decisionMatrix
	 * @param {*} state - current state
	 * @param {Array} matrix - decision matrix
	 */
	MAP_MATRIX(state, matrix) {
		state.decisionMatrix = matrix;
	},
	/**
	 * Set the profile configuration evaluation list
	 * @param {*} state - current state
	 * @param {Array} matrix - decision matrix in API format
	 */
	MAP_MATRIX_BACK(state, matrix) {
		state.configProfile.overallDecisionConfiguration.evaluationList = matrix;
	},
	/**
	 * Set the factor configurations
	 * @param {*} state - current state
	 * @param {Array} factorThresholds - array of factors
	 */
	MAP_FACTOR_CONFIGURATION(state, factorThresholds) {
		state.factorConfigurations = factorThresholds;
	},
	/**
	 * Set the profile configuration factor configuration
	 * @param {*} state - current state
	 * @param {Object} factorConfig - tree structure for factor configuration
	 */
	MAP_FACTOR_CONFIGURATION_BACK(state, factorConfig) {
		state.configProfile.overallDecisionConfiguration.factorConfiguration = factorConfig;
	},
	/**
	 * Set the parent children map
	 * @param {*} state - current state
	 * @param {Map} parentChildrenMap - map with the parentId as the key
	 * and an array of children as the value
	 */
	CREATE_PARENT_CHILDREN_MAP(state, parentChildrenMap) {
		state.parentChildrenMap = parentChildrenMap;
	},
	/**
	 * Set the factor configuration map
	 * @param {*} state - current state
	 * @param {Map} map - factor configuration map with factorId as the key
	 */
	CREATE_FACTOR_CONFIG_MAP(state, map) {
		state.factorConfigMap = map;
	},
	/**
	 * Set the addable friendly fraud groups and factors
	 * @param {*} state - current state
	 * @param {Array} factors - addable friendly fraud groups and factors
	 */
	CREATE_ADDABLE_FRIENDLY_FRAUD_GROUPS_AND_FACTORS(state, factors) {
		state.addableFriendlyFraudGroupsAndFactors = factors;
	},
	/**
	 * Set the addable true fraud groups and factors
	 * @param {*} state - current state
	 * @param {Array} factors - addable true fraud groups and factors
	 */
	CREATE_ADDABLE_TRUE_FRAUD_GROUPS_AND_FACTORS(state, factors) {
		state.addableTrueFraudGroupsAndFactors = factors;
	},
	/**
	 * Set friendly fraud factors only
	 * @param {*} state - current state
	 * @param {Array} factors - friendly fraud factors only
	 */
	CREATE_FRIENDLY_FRAUD_FACTORS_ONLY_LIST(state, factors) {
		state.addableFriendlyFraudFactorsOnly = factors;
	},
	/**
	 * Set true fraud factors only
	 * @param {*} state - current state
	 * @param {Array} factors - true fraud factors only
	 */
	CREATE_TRUE_FRAUD_FACTORS_ONLY_LIST(state, factors) {
		state.addableTrueFraudFactorsOnly = factors;
	},
	/**
	 * Set the initial decision config map
	 * @param {*} state - current state
	 * @param {Map} map - initial decision config map
	 */
	CREATE_INITIAL_DECISION_CONFIG_MAP(state, map) {
		state.initialDecisionConfigMap = map;
	},
	/**
	 * Set the initial decision outcome list labels
	 * @param {*} state - current state
	 * @param {Array} labels - outcome labels
	 */
	CREATE_INITIAL_DECISION_OUTCOME_LIST_LABELS(state, labels) {
		state.initialDecisionOutcomeListLabels = labels;
	},
	/**
	 * Set the initial decision items
	 * @param {*} state - current state
	 * @param {Array} items - initial decision items
	 */
	CREATE_INITIAL_DECISION_ITEMS(state, items) {
		state.initialDecisionItems = items;
	},
	/**
	 * Set the available decisions list
	 * @param {*} state - current state
	 * @param {Array} availableDecisionsList - list of the avialable initial decisions
	 */
	CREATE_AVAILABLE_DECISIONS_LIST(state, availableDecisionsList) {
		state.availableDecisionsList = availableDecisionsList;
	},
	/**
	 * Set the profile configuration initial decision evaluation list
	 * @param {*} state - current state
	 * @param {Array} evaluationList - initial decision evaluation list in the API format
	 */
	UPDATE_EVALUATION_LIST(state, evaluationList) {
		state.configProfile.initialDecisionConfiguration.evaluationList = evaluationList;
	},
	/**
	 * Set the editing tree depth
	 * @param {*} state - current state
	 * @param {Number} value - tree depth
	 */
	SET_EDITING_TREE_DEPTH(state, value) {
		state.editingTreeDepth = value;
	},
	/**
	 * Set the allowProfileDeploy flag
	 * @param {*} state - current state
	 * @param {Boolean} value - true if profile is able to be deployed
	 */
	SET_ALLOW_PROFILE_DEPLOY(state, value) {
		state.allowProfileDeploy = value;
	},
	/**
	 * Set the configurationValid flag
	 * @param {*} state - current state
	 * @param {Boolean} value - true if the configuration being viewed is valid
	 */
	SET_CONFIGURATION_VALID(state, value) {
		state.configurationValid = value;
	},
	/**
	 * Set thresholdFormValid flag
	 * @param {*} state - current state
	 * @param {Boolean} value - true if the threshold form being viewed is valid
	 */
	SET_THRESHOLD_FORM_VALID(state, value) {
		state.thresholdFormValid = value;
	},
	/**
	 * Set profile to edit label
	 * @param {*} state - current state
	 * @param {String} label - selected profile label in the configuration explorer
	 */
	SET_PROFILE_TO_EDIT_LABEL(state, label) {
		state.profileToEditLabel = label;
	},
	/**
	 * set the initialDecisionsValid flag
	 * @param {*} state - current state
	 * @param {Boolean} value - true if the initial decision fields are valid
	 */
	SET_INITIAL_DECISIONS_VALID(state, value) {
		state.initialDecisionsValid = value;
	},
	/**
	 * Set the reformatted deployed profile history list
	 * @param {*} state - current state
	 * @param {Array} list - reformatted profile history list
	 */
	REFORMAT_PROFILE_HISTORY_LIST(state, list) {
		state.deployedProfileHistoryList = list;
	},
	/**
	 * Set the champion challenger profile - aka the 'old' profile
	 * @param {*} state - current state
	 * @param {Object} profile - challenger profile data
	 */
	SET_CHALLENGER_PROFILE(state, profile) {
		state.challengerProfile = profile;
	},
	/**
	 * Set the challengerSuccessful flag
	 * @param {*} state - current state
	 * @param {Boolean} flag - true if CC was successful
	 */
	SET_CHALLENGER_SUCCESSFUL_FLAG(state, flag) {
		state.challengerSuccessful = flag;
	},
	/**
	 * Set the CC dispute count
	 * @param {*} state - current state
	 * @param {Number} count - current dispute count
	 */
	SET_DISPUTE_COUNT(state, count) {
		state.disputeCount = count;
	},
	/**
	 * Set the challenger date list labels
	 * @param {*} state - current state
	 * @param {Array} labels - list of labels for CC dates
	 */
	SET_CHALLENGER_DATE_LIST_LABELS(state, labels) {
		state.challengerDateListLabels = labels;
	},
	/**
	 * Set Champion Challenger date range
	 * @param {*} state - current state
	 * @param {Number} range - amount of days for CC to look back for disputes
	 */
	SET_CHALLENGER_RANGE(state, range) {
		state.challengerRange = range;
	},
	/**
	 * Set the CC tenantId for running CC as an admin user
	 * @param {*} state - current state
	 * @param {String} tenantId - selected tenantId
	 */
	SET_CHALLENGER_TENANT_ID(state, tenantId) {
		state.challengerTenantId = tenantId;
	},
	/**
	 * Set the CC start date for an accurate dispute count
	 * @param {*} state - current state
	 * @param {String} date - CC start date
	 */
	SET_CHALLENGER_START_DATE(state, date) {
		state.challengerStartDate = date;
	},
	/**
	 * Set the CC end date for an accurate dispute count
	 * @param {*} state - current state
	 * @param {String} date - CC end date - the current date
	 */
	SET_CHALLENGER_END_DATE(state, date) {
		state.challengerEndDate = date;
	},
	/**
	 * Set challengerRunning flag
	 * @param {*} state - current state
	 * @param {Boolean} value - true if CC is running
	 */
	SET_CHALLENGER_RUNNING(state, value) {
		state.challengerRunning = value;
	},
	/**
	 * Set the CC report Id
	 * @param {*} state - current state
	 * @param {String} id - reportId generated by CC
	 */
	SET_CHALLENGER_REPORT_ID(state, id) {
		state.challengerReportId = id;
	},
	/**
	 * Set the CC disputes completed
	 * @param {*} state - current state
	 * @param {Number} count - amount of disputes completed for CC
	 */
	SET_DISPUTES_COMPLETED(state, count) {
		state.disputesCompleted = count;
	},
	/**
	 * Activate a profile in the configuation explorer as an admin user
	 * @param {*} state - current state
	 * @param {Object} payload - tenantId, profileId, and isActive flag
	 */
	ACTIVATE_PROFILE(state, payload) {
		var profile = state.profileList.find(item => item.profileId === payload.profileId);
		profile.isActive = payload.isActive;
	},
	/**
	 * Set challenger result list
	 * @param {*} state - current state
	 * @param {Array} list - CC result list
	 */
	SET_CHALLENGER_RESULT_LIST(state, list) {
		state.challengerResultList = list;
	},

	/**
	 * Set the CC different outcome list
	 * @param {*} state - current state
	 * @param {Array} list - different outcome list
	 */
	SET_CHALLENGER_DIFFERENT_OUTCOME_LIST(state, list) {
		state.challengerDifferentOutcomeList = list;
	},
	/**
	 * Set the CC old decision counts
	 * @param {*} state - current state
	 * @param {Object} counts - old decision counts
	 */
	SET_CHALLENGER_OLD_DECISION_COUNTS(state, counts) {
		state.challengerOldDecisionCounts = counts;
	},
	/**
	 * Set the CC new decision counts
	 * @param {*} state - current state
	 * @param {Object} counts - new decision counts
	 */
	SET_CHALLENGER_NEW_DECISION_COUNTS(state, counts) {
		state.challengerNewDecisionCounts = counts;
	},
	/**
	 * Set CC old decision percentages
	 * @param {*} state - current state
	 * @param {Object} percentages - old decision percentages
	 */
	SET_CHALLENGER_OLD_DECISION_PERCENTAGES(state, percentages) {
		state.challengerOldDecisionPercentages = percentages;
	},
	/**
	 * Set CC new decision percentages
	 * @param {*} state - current state
	 * @param {Object} percentages - new decision percentages
	 */
	SET_CHALLENGER_NEW_DECISION_PERCENTAGES(state, percentages) {
		state.challengerNewDecisionPercentages = percentages;
	},
	/**
	 * Set CC delta percentages
	 * @param {*} state - current state
	 * @param {Object} percentages - delta percentages
	 */
	SET_CHALLENGER_DECISION_DELTA_PERCENTAGES(state, percentages) {
		state.challengerDecisionDeltaPercentages = percentages;
	},
	/**
	 * Set CC status timer
	 * @param {*} state - current state
	 * @param {Number} value - status timer identifier
	 */
	SET_CHALLENGER_STATUS_TIMER(state, value) {
		state.challengerStatusTimer = value;
	},
	/**
	 * Set profile performance claim data
	 * @param {*} state - current state
	 * @param {Object} data - profile performance claim data
	 */
	SET_PROFILE_PERFORMANCE_CLAIM_DATA(state, data) {
		state.profilePerformanceClaimData = data;
	},
	/**
	 * Set profile performance dispute data
	 * @param {*} state - current state
	 * @param {Object} payload - dispute data
	 */
	SET_PROFILE_PERFORMANCE_DISPUTE_DATA(state, payload) {
		state.profilePerformanceDisputeData = payload.disputeData;
		state.profilePerformanceSeries = payload.performanceSeries;
		state.profilePerformanceDisputeAmounts = payload.disputeAmounts;
		state.profilePerformancePaidDisputeAverage = payload.paidDisputeAverage;
	},
	/**
	 * Set CC old dispute details
	 * @param {*} state - current state
	 * @param {Object} details - old dispute details
	 */
	SET_CHALLENGER_DISPUTE_DETAILS_OLD(state, details) {
		state.challengerDisputeDetailsOld = details;
	},
	/**
	 * Set CC new dispute details
	 * @param {*} state - current state
	 * @param {Object} details - new dispute details
	 */
	SET_CHALLENGER_DISPUTE_DETAILS_NEW(state, details) {
		state.challengerDisputeDetailsNew = details;
	},
	/**
	 * Set CC dispute details overall results old
	 * @param {*} state - current state
	 * @param {Object} results - dispute details overall results old
	 */
	SET_CHALLENGER_DISPUTE_DETAILS_OVERALL_RESULTS_OLD(state, results) {
		state.challengerDisputeDetailsOverallResultsOld = results;
	},
	/**
	 * Set CC dispute details overall results new
	 * @param {*} state - current state
	 * @param {Object} results - dispute details overall results new
	 */
	SET_CHALLENGER_DISPUTE_DETAILS_OVERALL_RESULTS_NEW(state, results) {
		state.challengerDisputeDetailsOverallResultsNew = results;
	},
	/**
	 * Append CC dispute details factor decisions old
	 * @param {*} state - current state
	 * @param {Object} node - factor decision node
	 */
	APPEND_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_OLD(state, node) {
		state.challengerDisputeDetailsFactorDecisionsOld.push(node);
	},
	/**
	 * Append CC dispute details factor decisions new
	 * @param {*} state - current state
	 * @param {Object} node - factor decision node
	 */
	APPEND_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_NEW(state, node) {
		state.challengerDisputeDetailsFactorDecisionsNew.push(node);
	},
	/**
	 * Clear CC dispute details factor decisions old
	 * @param {*} state - current state
	 */
	CLEAR_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_OLD(state) {
		state.challengerDisputeDetailsFactorDecisionsOld = [];
	},
	/**
	 * Clear CC dispute details factor decisions new
	 * @param {*} state - current state
	 */
	CLEAR_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISIONS_NEW(state) {
		state.challengerDisputeDetailsFactorDecisionsNew = [];
	},
	/**
	 * Set CC dispute details factor decision map old
	 * @param {*} state - current state
	 * @param {Map} map - dispute details factor decision map old
	 */
	SET_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISION_MAP_OLD(state, map) {
		state.challengerDisputeDetailsFactorDecisionMapOld = map;
	},
	/**
	 * Set CC dispute details factor decision map new
	 * @param {*} state - current state
	 * @param {Map} map - dispute details factor decision map new
	 */
	SET_CHALLENGER_DISPUTE_DETAILS_FACTOR_DECISION_MAP_NEW(state, map) {
		state.challengerDisputeDetailsFactorDecisionMapNew = map;
	},
	/**
	 * Set CC dispute details factor changes
	 * @param {*} state - current state
	 * @param {Array} changes - list of factor changes
	 */
	SET_CHALLENGER_DISPUTE_DETAILS_FACTOR_CHANGES(state, changes) {
		state.challengerDisputeDetailsFactorChanges = changes;
	},
	/**
	 * Set initial decision meta data details
	 * @param {*} state - current state
	 * @param {Object} details - initial decision meta data details
	 */
	SET_INITIAL_DECISION_META_DATA_DETAILS(state, details) {
		state.initialDecisionMetaDataDetails = details;
	},
	/**
	 * Set factor meta data details
	 * @param {*} state - current state
	 * @param {Object} details - evaluation factor meta data details
	 */
	SET_FACTOR_META_DATA_DETAILS(state, details) {
		state.factorMetaDataDetails = details;
	},
	/**
	 * Set factor meta data built flag
	 * @param {*} state - current state
	 * @param {Boolean} flag - true if evaluation factor meta data is built
	 */
	SET_FACTOR_META_DATA_BUILT_FLAG(state, flag) {
		state.factorMetaDataBuilt = flag;
	},
};


/*
	Getters are non-cached access points to the state data.
	They allow us to interrogate the data and pull back dynamic subsets.
*/
export const getters = {
	/**
	 * Get the profile configuration
	 * @param {*} state - current state
	 * @returns configProfile object
	 */
	getProfile: (state) => { return state.configProfile },
	/**
	 * Get profile audit log messages
	 * @param {*} state - current state
	 * @returns profileAuditMessages array
	 */
	getProfileAuditMessages: (state) => { return state.profileAuditMessages },
	/**
	 * Get the overall configuration
	 * @param {*} state - current state
	 * @returns overallConfiguration object
	 */
	getOverallConfig: (state) => { return state.overallConfiguration },
	/**
	 * Get the decision matrix
	 * @param {*} state - current state
	 * @returns decisionMatrix array
	 */
	getDecisionMatrix: (state) => { return state.decisionMatrix },
	/**
	 * Get the available factor list
	 * @param {*} state - current state
	 * @returns availableFactorList array
	 */
	getAvailableFactorList: (state) => { return state.availableFactorList },
	/**
	 * Get the available factor list map
	 * @param {*} state - current state
	 * @returns availableFactorListMap
	 */
	getAvailableFactorListMap: (state) => { return state.availableFactorListMap },
	/**
	 * Get the factor configurations
	 * @param {*} state - current state
	 * @returns factorConfigurations array
	 */
	getFactorConfigurations: (state) => { return state.factorConfigurations },
	/**
	 * Get the factor configuration map
	 * @param {*} state - current state
	 * @returns factorConfigMap
	 */
	getFactorConfigMap: (state) => { return state.factorConfigMap },
	/**
	 * Get the factor tree
	 * @param {*} state - current state
	 * @returns factorTree array
	 */
	getFactorTree: (state) => { return state.factorTree },
	/**
	 * Get the parent children map
	 * @param {*} state - current state
	 * @returns parentChildrenMap
	 */
	getParentChildrenMap: (state) => { return state.parentChildrenMap },
	/**
	 * Get the addable friendly fraud groups and factors
	 * @param {*} state - current state
	 * @returns addableFriendlyFraudGroupsAndFactors array
	 */
	getAddableFriendlyFraudGroupsAndFactors: (state) => { return state.addableFriendlyFraudGroupsAndFactors },
	/**
	 * Get the addable true fraud groups and factors
	 * @param {*} state - current state
	 * @returns addableTrueFraudGroupsAndFactors array
	 */
	getAddableTrueFraudGroupsAndFactors: (state) => { return state.addableTrueFraudGroupsAndFactors },
	/**
	 * Get addable factors only
	 * @param {*} state - current state
	 * @returns addableFactorsOnly array
	 */
	getAddableFactorsOnly: (state) => { return state.addableFactorsOnly },
	/**
	 * Get editing tree depth
	 * @param {*} state - current state
	 * @returns editingTreeDepth value
	 */
	getEditingTreeDepth: (state) => { return state.editingTreeDepth },
	/**
	 * Get allowProfileDeploy flag
	 * @param {*} state - current state
	 * @returns allowProfileDeploy boolean flag
	 */
	getAllowProfileDeploy: (state) => { return state.allowProfileDeploy },
	/**
	 * Get initial decision outcome list
	 * @param {*} state - current state
	 * @returns initialDecisionOutcomelist array
	 */
	getInitialDecisionOutcomeList: (state) => { return state.initialDecisionOutcomeList },
	/**
	 * Get initial deccision configuration list
	 * @param {*} state - current state
	 * @returns initialDecisionConfigList array
	 */
	getInitialDecisionConfigList: (state) => { return state.initialDecisionConfigList },
	/**
	 * Get initial decision configuration map
	 * @param {*} state - current state
	 * @returns initialDecisionConfigMap
	 */
	getInitialDecisionConfigMap: (state) => { return state.initialDecisionConfigMap },
	/**
	 * Get profile evaluation list
	 * @param {*} state - current state
	 * @returns evaluationList array
	 */
	getEvaluationList: (state) => { return state.evaluationList },
	/**
	 * Get profile initial decision items
	 * @param {*} state - current state
	 * @returns initialDecisionItems array
	 */
	getInitialDecisionItems: (state) => { return state.initialDecisionItems },
	/**
	 * Get deployed profile history list
	 * @param {*} state - current state
	 * @returns deployedProfileHistoryList array
	 */
	getDeployedProfileHistoryList: (state) => { return state.deployedProfileHistoryList },
	/**
	 * Get historical audit log (tenant level)
	 * @param {*} state - current state
	 * @returns historicalAuditLog array
	 */
	getHistoricalAuditLog: (state) => { return state.historicalAuditLog },
	/**
	 * Get the CC profile - 'old' profile
	 * @param {*} state - current state
	 * @returns challengerProfile object
	 */
	getChallengerProfile: (state) => { return state.challengerProfile },
	/**
	 * Get CC date list
	 * @param {*} state - current state
	 * @returns challengerDateList array
	 */
	getChallengerDateList: (state) => { return state.challengerDateList },
	/**
	 * Get CC date range
	 * @param {*} state - current state
	 * @returns challengerRange value
	 */
	getChallengerRange: (state) => { return state.challengerRange },
	/**
	 * Get CC dispute count
	 * @param {*} state - current state
	 * @returns disputeCount value
	 */
	getDisputeCount: (state) => { return state.disputeCount },
	/**
	 * Get CC disputes completed
	 * @param {*} state - current state
	 * @returns disputesCompleted value
	 */
	getDisputesCompleted: (state) => { return state.disputesCompleted },
	/**
	 * Get challenger successful flag
	 * @param {*} state - current state
	 * @returns challengerSuccessful boolean flag
	 */
	getChallengerSuccessful: (state) => { return state.challengerSuccessful },
	/**
	 * Get challenger running flag
	 * @param {*} state - current state
	 * @returns challengerRunning boolean flag
	 */
	getChallengerRunning: (state) => { return state.challengerRunning },
	/**
	 * Get CC result list
	 * @param {*} state - current state
	 * @returns challengerResultList array
	 */
	getChallengerResultList: (state) => { return state.challengerResultList },
	/**
	 * Get CC old decision counts
	 * @param {*} state - current state
	 * @returns challengerOldDecisionCounts object
	 */
	getChallengerOldDecisionCounts: (state) => { return state.challengerOldDecisionCounts },
	/**
	 * Get CC new decision counts
	 * @param {*} state - current state
	 * @returns challengerNewDecisionCounts object
	 */
	getChallengerNewDecisionCounts: (state) => { return state.challengerNewDecisionCounts },
	/**
	 * Get CC status timer
	 * @param {*} state - current state
	 * @returns challengerStatusTimer identifier value
	 */
	getChallengerStatusTimer: (state) => { return state.challengerStatusTimer },
	/**
	 * Get CC dispute details old
	 * @param {*} state - current state
	 * @returns challengerDisputeDetailsOld object
	 */
	getChallengerDisputeDetailsOld: (state) => { return state.challengerDisputeDetailsOld },
	/**
	 * Get CC dispute details new
	 * @param {*} state - current state
	 * @returns challengerDisputeDetailsNew object
	 */
	getChallengerDisputeDetailsNew: (state) => { return state.challengerDisputeDetailsNew },
	/**
	 * Get CC dispute details factor decisions old
	 * @param {*} state - current state
	 * @returns challengerDisputeDetailsFactorDecisionsOld array
	 */
	getChallengerDisputeDetailsFactorDecisionsOld: (state) => { return state.challengerDisputeDetailsFactorDecisionsOld },
	/**
	 * Get CC dispute details factor decisions new
	 * @param {*} state - current state
	 * @returns challengerDisputeDetailsFactorDecisionsNew array
	 */
	getChallengerDisputeDetailsFactorDecisionsNew: (state) => { return state.challengerDisputeDetailsFactorDecisionsNew },
	/**
	 * Get CC dispute details factor decision map old
	 * @param {*} state - current state
	 * @returns challengerDisputeDetailsFactorDecisionMapOld
	 */
	getChallengerDisputeDetailsFactorDecisionMapOld: (state) => { return state.challengerDisputeDetailsFactorDecisionMapOld },
	/**
	 * Get CC dipute details factor decisons map new
	 * @param {*} state - current state
	 * @returns challengerDisputeDetailsFactorDecisionMapNew
	 */
	getChallengerDisputeDetailsFactorDecisionMapNew: (state) => { return state.challengerDisputeDetailsFactorDecisionMapNew },
	/**
	 * Get profile performance dispute data
	 * @param {*} state - current state
	 * @returns profilePerformanceDisputeData object
	 */
	getProfilePerformanceDisputeData: (state) => { return state.profilePerformanceDisputeData },
	/**
	 * Get factor meta data details
	 * @param {*} state - current state
	 * @returns factorMetaDataDetails object
	 */
	getFactorMetaDataDetails: (state) => { return state.factorMetaDataDetails }
};

